import { useCognitoAuth } from 'features/auth';
import {
  FullscreenLoading,
  InputSelect,
  SiteHeader,
  SiteHeaderNavLink,
  useModal,
  useMuiSwitchableTheme,
  useToastr,
} from '@farmshare/ui-components';
import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Announcement,
  AutoGraph,
  CalendarMonth,
  CalendarMonthOutlined,
  ChatBubbleOutline,
  ChecklistOutlined,
  FolderOutlined,
  GroupOutlined,
  Inventory2Outlined,
  LockOutline,
  PermIdentity,
  Person,
  Search,
  SettingsOutlined,
  StickyNote2Rounded,
  WorkOutline,
} from '@mui/icons-material';
import { Badge, Box, IconButton } from '@mui/material';
import { environment } from 'environments/environment';
import { find, first, map, sortBy } from 'lodash';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { setUser as sentrySetUser } from '@sentry/react';
import { ReadOnlyBanner } from 'components/read-only-banner';
import { ServiceWorkerProvider } from 'components/service-worker-provider';
// Renders the bare <Outlet /> on every route but /marketplace → /marketplace/search,
// which it tows as one coupled track. See features/page-tow.
import PageTowOutlet from 'features/page-tow/page-tow-outlet';
import { PinFlyer } from 'features/pin-flight/pin-flyer';

import {
  type CalculateProcessorCapacityAllSpeciesQuery,
  EnumUserRole,
  useCalculateProcessorCapacityAllSpeciesLazyQuery,
  useCreateProcessorSchedulingMutation,
  useCustomFieldValueBulkSaveMutation,
  useNavBadgeCountsQuery,
  type User,
  useRecurringSeriesCreateFromJobMutation,
  useUserTenantVendorLazyQuery,
  useUserUpdateActiveVendorMutation,
  type Vendor,
} from './lib/graphql';
import { useAuthStorageCleanup } from 'lib/hooks';
import { ModuleId, useActiveModules, useInventoryModule } from 'lib/modules';
import { ordersEntryPath } from 'pages/processor/further-processing/orders-nav';

import logo from './assets/images/logo-roca-green.svg';
import whiteLogo from './assets/images/logo-roca-white.svg';
import { userState, vendorState } from './state';

import type { AuthProps } from '@farmshare/ui-components/src/components/auth/auth';
import {
  identifyContentsquareUser,
  loadContentsquare,
  trackContentsquarePageview,
} from 'lib/contentsquare';
import { afterPageLoad } from 'lib/after-page-load';
import {
  bootIntercomAfterPageLoad,
  onIntercomUnreadCountChange,
  showIntercomMessages,
  showIntercomSpace,
  updateIntercom,
} from 'lib/intercom';
import { getPlatformStripe } from 'lib/stripe';
import { isPlaywrightTestMode } from 'lib/test-mode';
import {
  canWrite,
  isAdmin,
  isBuyer,
  isBuyerOnly,
  isProcessor,
  isProducer,
  PROCESSOR_VIEW_ROLES,
  PROCESSOR_WRITE_ROLES,
} from 'lib/userHelpers';
import { AdminUserSearch } from 'components/admin-user-search';
// From the layout files, not the feature's barrel, which re-exports every
// portal page and would put all of them in the main bundle.
import { BuyerShell } from 'features/producer-buyer-portal/layout/buyer-shell';
import { ProducerShell } from 'features/producer-buyer-portal/layout/producer-shell';
import { useGlobalSearch } from 'features/global-search';

const FabMenu = lazy(() =>
  import('./pages/processor/calendar/_views/mobile').then((module) => ({
    default: module.FabMenu,
  })),
);
// TEMPORARY (inventory beta): the Feedback buttons — the floating pill (which
// also hosts the form) and the phone header icon. Lazy, and only mounted for
// beta processors, so nobody else downloads them; they render nothing off
// Inventory pages.
const InventoryBetaFeedbackLauncher = lazy(() =>
  import('features/inventory-beta-feedback').then((module) => ({
    default: module.InventoryBetaFeedbackLauncher,
  })),
);
const InventoryBetaFeedbackHeaderButton = lazy(() =>
  import('features/inventory-beta-feedback').then((module) => ({
    default: module.InventoryBetaFeedbackHeaderButton,
  })),
);

interface LuckyOrangeVisitorApi {
  $internal: { ready: (event: string) => Promise<void> };
  visitor: { identify: (id: string, props: Record<string, string>) => void };
}

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    LOQ: unknown[];
    luckyOrangeCompletedSetup: boolean;
  }
}

/** Intercom workspace this app talks to. */
const INTERCOM_APP_ID = 'n0tvm2gp';

/**
 * Messenger display settings. The launcher bubble stays hidden because the
 * nav owns the entry points ("Chat with Us" / "What's New"); alignment is
 * pinned right so the Intercom window doesn't land on top of the nav rail.
 * These must be repeated on every `update` call — Intercom re-applies the
 * workspace defaults for any display setting missing from the payload.
 */
const INTERCOM_DISPLAY_SETTINGS = {
  hide_default_launcher: true,
  alignment: 'right',
} as const;

function tagUserForGTM(user?: User) {
  if (!user || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'user',
    userId: user._id,
    email: user.email,
    name: `${user.first_name} ${user.last_name}`,
    vendorId: user.active_vendor?._id,
    vendorName: user.active_vendor?.shop_name,
    role: user.role,
  });
}

function tagUserForSentry(user: User | null = null) {
  sentrySetUser(
    user
      ? {
          id: user._id,
          email: `${user.email}`,
          name: `${user.first_name} ${user.last_name}`,
          vendorId: user.active_vendor?._id,
          vendorName: user.active_vendor?.shop_name,
          role: user.role,
        }
      : null,
  );
}

function tagUserForIntercom(user: User | null = null) {
  if (!user) return updateIntercom({ ...INTERCOM_DISPLAY_SETTINGS });

  return updateIntercom({
    ...INTERCOM_DISPLAY_SETTINGS,
    api_base: 'https://api-iam.intercom.io',
    intercom_user_jwt: user.intercomWebToken,
    user_id: user._id,
    email: user.email || undefined,
    name:
      `${user.first_name || ''} ${user.last_name || ''}`.trim() || undefined,
    Role: user.role,
    company: user.active_vendor
      ? {
          company_id: user.active_vendor._id,
          name: user.active_vendor.shop_name,
        }
      : undefined,
  });
}

let didSetupLuckyOrange = false;

function setupLuckyOrange(localUser?: User) {
  // The production stage only — not every production build, which dev and
  // staging are too.
  if (!didSetupLuckyOrange && localUser && environment.env === 'production') {
    didSetupLuckyOrange = true;
    window.LOQ = window.LOQ || [];
    window.luckyOrangeCompletedSetup = true;

    window.LOQ.push([
      'ready',
      async (LO: LuckyOrangeVisitorApi) => {
        await LO.$internal.ready('visitor');
        LO.visitor.identify(localUser._id, {
          email: `${localUser.email}`,
          phone: `${localUser.phone}`,
          name: `${localUser.first_name} ${localUser.last_name}`,
          role: localUser.role ?? '',
        });
      },
    ]);
  }
}

/**
 * Persona for Contentsquare: 'producer' | 'buyer' | 'both', or undefined for
 * non-Basic users (processors). Reuses the app's own buyer-detection helpers so
 * Contentsquare's notion of a buyer can't drift from what the app renders.
 * Onboarding is a processor concern; this exists to separate the two sides of
 * the producer/buyer portal (jobs vs splits), not to analyze onboarding.
 * @param user
 */
function contentsquareUserType(user: User | null): string | undefined {
  if (!user || !isProducer(user)) return undefined; // non-Basic → no persona
  if (isBuyerOnly(user)) return 'buyer';
  if (isBuyer(user)) return 'both'; // has buyer *and* producer relationships
  return 'producer';
}

export function App() {
  const isTestMode = isPlaywrightTestMode();

  // Booted on MOUNT, not at module scope. `main.tsx` imports this module
  // statically, so anything at module scope runs on every page — including the
  // hunter-facing kiosk, which is mounted outside <App /> precisely to keep the
  // processor's app off it. Booting Intercom up there put the Farmshare support
  // messenger in front of a hunter standing in a lobby, proactively offering
  // help with food-safety compliance and regulatory enforcement actions.
  //
  // The boot itself, and Stripe.js, wait until the page has finished loading:
  // neither is needed in the first few seconds and together they are ~1.2 MB
  // competing with the route's own code and data.
  useEffect(() => {
    const cancelIntercom = bootIntercomAfterPageLoad({
      app_id: INTERCOM_APP_ID,
      ...INTERCOM_DISPLAY_SETTINGS,
    });
    const cancelStripe = afterPageLoad(() => {
      void getPlatformStripe();
    });
    return () => {
      cancelIntercom();
      cancelStripe();
    };
  }, []);

  const {
    isAuthenticated,
    isLoading,
    logout,
    setImpersonateUserId,
    clearImpersonation,
    impersonateUserId,
  } = useCognitoAuth();

  const { save, multiStep } = useModal();
  const { openGlobalSearch } = useGlobalSearch();
  const { push: pushToast } = useToastr();

  const { marginLeft, marginTop } = useMuiSwitchableTheme();
  const [user, setUserState] = useRecoilState(userState);
  const [vendor, setVendor] = useRecoilState(vendorState);
  const [allVendors, setAllVendors] = useState<Vendor[]>([]);

  const activeModules = useActiveModules();
  // Inventory's nav menu is filtered twice: `requiredModule` hides the whole
  // menu, and the per-component flags decide which destinations remain in it.
  const inventory = useInventoryModule();
  // Orders opens on whichever of its tabs the vendor licenses first.
  const ordersHref = ordersEntryPath((c) => inventory.has(c));

  const [ready, setReady] = useState(false);
  const [createScheduling] = useCreateProcessorSchedulingMutation();
  const [bulkSaveCustomFieldValues] = useCustomFieldValueBulkSaveMutation();
  const [createRecurringSeries] = useRecurringSeriesCreateFromJobMutation();
  const [calculateProcessorCapacityAllSpeciesQuery] =
    useCalculateProcessorCapacityAllSpeciesLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const [capacityData, setCapacityData] = useState<
    CalculateProcessorCapacityAllSpeciesQuery | null | undefined
  >(null);
  const { cleanupApolloCache, markLogout } = useAuthStorageCleanup();

  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const isProcessorPage = pathname.startsWith('/processor');
  const [alerts, setAlerts] = useState<Record<string, number>>({});

  const [isTabVisible, setIsTabVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden,
  );
  useEffect(() => {
    const onVisibilityChange = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Single round-trip for every alert badge in the main nav. Skipped for
  // unauthenticated visitors; resolver returns nulls for inapplicable fields.
  // Polling pauses while the tab is backgrounded.
  const { data: navBadgeData } = useNavBadgeCountsQuery({
    skip: !user,
    fetchPolicy: 'cache-and-network',
    pollInterval: isTabVisible ? 15_000 : 0,
  });
  const navBadgeCounts = navBadgeData?.navBadgeCounts;

  onIntercomUnreadCountChange((unreadCount: number) => {
    if (unreadCount === alerts.support) {
      return;
    }
    setAlerts((prevAlerts) => ({
      ...prevAlerts,
      support: unreadCount,
    }));
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  const [search] = useSearchParams();

  const [getUser, getUserOperation] = useUserTenantVendorLazyQuery({
    notifyOnNetworkStatusChange: false,
  });
  const [updateUserActiveVendor] = useUserUpdateActiveVendorMutation();

  const handleAddJob = useCallback(async () => {
    // Load capacity data before opening modal
    const startDate = moment().format('YYYY-MM-DD');
    const endDate = moment().add(3, 'months').format('YYYY-MM-DD');
    const result = await calculateProcessorCapacityAllSpeciesQuery({
      variables: {
        vendorId: vendor?._id || '',
        startDate,
        endDate,
        dateFormat: 'M/DD/YYYY',
        groupFormat: 'MM/YYYY',
      },
    });
    setCapacityData(result.data);

    const { createQuickScheduleMultiStepConfig } = await import(
      'features/jobs/components/quick-schedule-steps'
    );

    multiStep((_onClose: () => void) => {
      const config = createQuickScheduleMultiStepConfig({
        vendorId: vendor?._id || '',
        initialValues: {},
        createScheduling,
        bulkSaveCustomFieldValues,
        createRecurringSeries,
        capacityData,
        pushToast,
        onSuccess: () => {
          // Navigate to the job details page after creation
          // The scheduleId will be available in the success handler
        },
      });
      return config;
    });
  }, [
    calculateProcessorCapacityAllSpeciesQuery,
    capacityData,
    createScheduling,
    bulkSaveCustomFieldValues,
    createRecurringSeries,
    multiStep,
    pushToast,
    vendor?._id,
  ]);

  // Redirect to login. On the public buyer pages (accessible anonymously), send
  // the visitor to the buyer-branded login so the buyer intent is preserved.
  const handleLogin = async (): Promise<void> => {
    const isPublicBuyerPage =
      pathname.startsWith('/buyer/processor') ||
      pathname.startsWith('/buyer/available') ||
      pathname.startsWith('/buyer/animal') ||
      pathname.startsWith('/processors');
    if (isPublicBuyerPage) {
      // Return the visitor to the storefront after auth — mirrors scheduling's
      // requireLogin → /login?redirect=<current> behavior, buyer-branded.
      const redirect = encodeURIComponent(pathname + location.search);
      navigate(`/login?redirect=${redirect}`);
      return;
    }
    navigate('/login');
  };

  useEffect(() => {
    if (isAuthenticated) {
      getUser();
    }
    setReady(true);
  }, [getUser, isAuthenticated]);

  useEffect(() => {
    if (getUserOperation.data?.userTenantVendor) {
      let localVendor: Vendor;
      const localUser = getUserOperation.data.userTenantVendor as User;

      if (getUserOperation.data.userTenantVendor.active_vendor) {
        localVendor = getUserOperation.data.userTenantVendor
          .active_vendor as Vendor;
      } else {
        localVendor = first(
          getUserOperation.data.userTenantVendor.tenant?.vendors,
        ) as Vendor;
      }

      if (localUser && localUser._id !== user?._id) {
        setUserState(localUser);
      }

      if (localVendor && localVendor._id !== vendor?._id) {
        setVendor(localVendor as Vendor);
      }
    }
  }, [
    getUserOperation.data?.userTenantVendor,
    setUserState,
    setVendor,
    vendor,
    user,
  ]);

  // Check if the user is loaded and matches the current user state
  // This is to ensure that the user data is fully loaded before proceeding
  const isUserLoaded = useMemo(() => {
    // If not authenticated, skip user loading check
    if (!isAuthenticated) {
      return true;
    }

    if (!ready || getUserOperation.loading) {
      return false;
    }
    return user?._id === getUserOperation.data?.userTenantVendor?._id;
  }, [
    ready,
    user,
    getUserOperation.loading,
    getUserOperation.data?.userTenantVendor,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (search.has('error')) {
      // eslint-disable-next-line no-console
      console.error('[Auth Error]', search.get('error_description'));

      markLogout();

      // Skip Auth0 logout in test mode — there's no real session to end.
      if (!isTestMode) {
        logout();
      }
    }
  }, [logout, search, markLogout, isTestMode]);

  /**
   * Filter nav links based on module and role requirements
   * @param links
   */
  const filterNavLinks = (links: SiteHeaderNavLink[]): SiteHeaderNavLink[] =>
    links
      .filter((link) => {
        // Check module requirement
        if (
          link.requiredModule &&
          !activeModules.some(
            (module) => module.config.id === link.requiredModule,
          )
        ) {
          return false;
        }

        const role = user?.role ?? 'guest';

        // Check role requirement
        if (
          link.requiredRoles &&
          !link.requiredRoles.includes(role as string)
        ) {
          return false;
        }
        return true;
      })
      .map((link) => {
        // Filter dropdown items
        if (link.dropDownItems) {
          return {
            ...link,
            dropDownItems: link.dropDownItems.filter((item) => {
              if (
                item.requiredModule &&
                !activeModules.some(
                  (module) => module.config.id === item.requiredModule,
                )
              ) {
                return false;
              }
              if (
                item.requiredRoles &&
                user &&
                !item.requiredRoles.includes(user.role as string)
              ) {
                return false;
              }
              return true;
            }),
          };
        }
        return link;
      });

  const buildNavLinks = (): SiteHeaderNavLink[] => {
    const processorRoles = PROCESSOR_VIEW_ROLES;

    return [
      // Non-processor
      {
        label: 'Dashboard',
        href: '/dashboard',
        icon: <ChecklistOutlined />,
        requiredRoles: [EnumUserRole.Basic],
      },
      {
        label: 'Chat',
        href: '/chat',
        icon: <ChatBubbleOutline />,
        requiredModule: ModuleId.CHAT,
        requiredRoles: [EnumUserRole.Basic],
      },
      // The guest "Marketplace" link was removed when the buyer marketplace
      // was switched off (PRO-784).
      {
        // The processor finder. Authenticated Basic users keep the
        // "Scheduling" wording they know.
        label: isAuthenticated ? 'Scheduling' : 'Find a Processor',
        href: '/marketplace?mode=process',
        icon: <CalendarMonthOutlined />,
        requiredRoles: [EnumUserRole.Basic, 'guest'],
      },
      // Processor
      {
        label: 'Dashboard',
        href: '/processor/dashboard',
        icon: <ChecklistOutlined />,
        requiredRoles: processorRoles,
        // TODO Move to calendar when calendar dashboard is ready
        alerts: navBadgeCounts?.requestedJobs ?? 0,
      },
      {
        label: 'Reporting',
        icon: <AutoGraph />,
        href: '/processor/reporting',
        requiredModule: ModuleId.REPORTING,
        requiredRoles: [EnumUserRole.Vendor],
      },
      {
        label: 'Reporting',
        icon: <AutoGraph />,
        requiredModule: ModuleId.REPORTING,
        requiredRoles: [EnumUserRole.VendorOwner, EnumUserRole.Admin],
        dropDownItems: [
          { label: 'Operations', href: '/processor/reporting' },
          { label: 'Processing Flow', href: '/processor/funnel' },
          { label: 'Revenue', href: '/processor/owner' },
          {
            label: 'Benchmarks',
            href: '/processor/benchmarks',
            requiredModule: ModuleId.PEER_BENCHMARKS,
          },
          { label: 'Forecasting', href: '/processor/forecasting' },
          { label: 'Settings', href: '/processor/reporting-config' },
        ],
      },
      {
        label: 'Calendar',
        href: '/processor/calendar',
        icon: <CalendarMonth />,
        requiredRoles: processorRoles,
      },
      {
        label: 'Customers',
        icon: <GroupOutlined />,
        requiredRoles: processorRoles,
        dropDownItems: [
          { label: 'All Customers', href: '/processor/customers' },
          // 'Merge & Dedupe' nav item temporarily removed; the
          // /processor/merge-dedupe route still exists. Restore when revisiting.
          {
            label: 'Chat',
            href: '/processor/chat',
            requiredModule: ModuleId.CHAT,
            alerts: navBadgeCounts?.unreadChats ?? 0,
          },
        ],
      },
      {
        label: 'Documents',
        href: '/processor/documents',
        icon: <FolderOutlined />,
        requiredModule: ModuleId.DOCUMENTS,
        requiredRoles: processorRoles,
      },
      {
        label: 'Jobs',
        icon: <WorkOutline />,
        requiredRoles: processorRoles,
        dropDownItems: [
          {
            label: 'All Jobs',
            href: '/processor/jobs',
            requiredRoles: processorRoles,
          },
          {
            label: 'Recurring',
            href: '/processor/recurring',
            requiredRoles: processorRoles,
          },
          {
            // Add Job opens the quick-schedule wizard, which fires
            // processorSchedulingCreate — a write. Gate it to write roles so a
            // read-only account doesn't get the affordance (PRO-588).
            label: 'Add Job',
            onClick: handleAddJob,
            requiredRoles: PROCESSOR_WRITE_ROLES,
          },
          {
            label: 'Animals',
            href: '/processor/animals',
            requiredRoles: processorRoles,
            beta: true,
          },
          {
            label: 'Waitlist',
            href: '/processor/settings/waitlist',
            alerts: navBadgeCounts?.waitlist ?? 0,
          },
          {
            label: 'Invoices',
            href: '/processor/invoices',
            requiredRoles: processorRoles,
            requiredModule: ModuleId.INVOICES,
          },
        ],
      },
      {
        label: 'Cut Sheets',
        icon: <StickyNote2Rounded />,
        requiredModule: ModuleId.CAPABILITIES_V2,
        requiredRoles: processorRoles,
        dropDownItems: [
          { label: 'Cut Sheet Builder', href: '/processor/cutsheets-v2' },
          {
            label: 'Cutsheet Library',
            href: '/processor/questionnaire-responses',
          },
        ],
      },
      {
        label: 'Inventory',
        icon: (
          <Badge badgeContent="Beta" color="secondary">
            <Inventory2Outlined />
          </Badge>
        ),
        requiredRoles: processorRoles,
        requiredModule: ModuleId.INVENTORY,
        // Ordered by how often staff hit them: what's on hand, the orders that
        // move it (customer orders going out, the further processing that
        // makes them, and purchase orders coming in — tabs of one Orders
        // page), and the back office behind it all (the catalog, locations,
        // labels, and the recall trace). By SKU / Packages / Boxes are lenses
        // inside Stock, and Scan/Stocktake are tasks launched from its header;
        // none of them belongs in the menu.
        //
        // Everything except Management is per-component and drops out when the
        // shop doesn't have it. Management always stays: it holds the SKU
        // catalog, which is the module's base and the only thing a shop
        // licensing INVENTORY is guaranteed to get.
        dropDownItems: [
          ...(inventory.has('stockTracking')
            ? [
                { label: 'Stock', href: '/processor/inventory' },
                { label: 'Insights', href: '/processor/inventory/insights' },
              ]
            : []),
          ...(ordersHref ? [{ label: 'Orders', href: ordersHref }] : []),
          // Selling in person: the full-screen register, for shops that
          // take retail orders.
          ...(inventory.has('orders')
            ? [{ label: 'Register', href: '/processor/register' }]
            : []),
          { label: 'Management', href: '/processor/inventory/management' },
        ],
      },
      {
        label: 'Settings',
        icon: <SettingsOutlined />,
        requiredRoles: processorRoles,
        dropDownItems: [
          { label: 'Processor Settings', href: '/processor/settings' },
          ...(inventory.has('wholesaleOrders')
            ? [
                {
                  label: 'Order assistant',
                  href: '/processor/orders/assistant',
                },
              ]
            : []),
          {
            // A station is the plant's own kit and pairing one is setup a
            // processor does once, whether or not they license inventory — so
            // it sits here, behind a module of its own, rather than inside the
            // inventory module's back office.
            label: 'Local Devices',
            href: '/processor/local-devices',
            requiredModule: ModuleId.LOCAL_DEVICES,
          },
          {
            // Owner-only: team management is a vendor-owner feature. A plain
            // admin (not impersonating a vendor) has no active_vendor, so
            // vendorTeamMembers would throw "No vendor is associated with your
            // account." and the page would immediately error into the Alert.
            label: 'Team',
            href: '/processor/team',
            requiredModule: ModuleId.TEAM_MANAGEMENT,
            requiredRoles: [EnumUserRole.VendorOwner],
          },
          {
            label: 'Capabilities (Legacy)',
            href: '/processor/capabilities',
            requiredModule: ModuleId.CAPABILITIES_V1,
          },
          {
            label: 'Cutsheets (Legacy)',
            href: '/processor/cutsheets',
            requiredModule: ModuleId.CAPABILITIES_V1,
          },

          {
            label: 'Integrations',
            href: '/integrations',
            requiredModule: ModuleId.INTEGRATIONS,
          },
          {
            label: 'Exports',
            href: '/exports',
            requiredModule: ModuleId.EXPORTS,
          },
          {
            label: 'Notifications',
            href: '/processor/notifications',
            requiredModule: ModuleId.NOTIFICATIONS,
          },
          {
            label: 'Landing Page',
            href: '/processor/landing-page',
            requiredModule: ModuleId.LANDING_PAGE,
          },
          {
            // Settings that are ONLY about wild game — kiosk access today,
            // more as later phases land. Species config stays on Processor
            // Settings with every other species.
            label: 'Wild Game',
            href: '/processor/wild-game',
            requiredModule: ModuleId.WILD_GAME,
          },
        ],
      },
      {
        label: 'Admin',
        icon: <LockOutline />,
        href: '/admin',
        requiredRoles: [EnumUserRole.Admin],
      },
      {
        label: 'Support & News',
        icon: <Announcement />,
        dropDownItems: [
          {
            label: "What's New",
            onClick: () => showIntercomSpace('news'),
          },
          {
            label: 'Chat with Us',
            onClick: () => showIntercomMessages(),
            alerts: alerts.support,
          },
        ],
      },
    ];
  };

  const dashboardNavLinks = useMemo(
    () => filterNavLinks(buildNavLinks()),
    [
      user?.role,
      alerts.support,
      handleAddJob,
      activeModules,
      inventory,
      navBadgeCounts?.waitlist,
      navBadgeCounts?.requestedJobs,
      navBadgeCounts?.unreadChats,
    ],
  );

  if (
    allVendors.length === 0 &&
    (getUserOperation.data?.userTenantVendor?.tenant?.vendors?.length || 0) > 1
  ) {
    setAllVendors(
      getUserOperation.data?.userTenantVendor?.tenant?.vendors as Vendor[],
    );
  }

  const dropdownItems = useMemo(() => {
    const items: AuthProps['dropdownItems'] = [
      { label: 'Profile', icon: <Person />, href: '/profile' },
    ];

    if ((allVendors.length || 0) > 1) {
      items.push({
        label: 'Switch Vendor',
        icon: <FontAwesomeIcon icon={faRepeat} className="me-2" />,
        onClick: () => {
          save<{ vendor: string }>({
            type: 'save',
            title: 'Switch Vendor',
            icon: faRepeat,
            // Start blank so the focused field is ready to type/pick without
            // first clearing the current vendor.
            initialValues: { vendor: '' },
            saveIcon: faRepeat,
            saveText: 'Switch',
            body: (
              <InputSelect
                label="Vendor"
                options={map(
                  sortBy(allVendors, (v) => v.shop_name),
                  (v) => ({ label: v.shop_name, value: v._id }),
                )}
                floatingLabel
                required
                autoFocus
              />
            ),
            onSubmit: (values) => {
              const selectedVendor = find(
                allVendors,
                (v) => v._id === values.vendor,
              );

              if (selectedVendor) {
                updateUserActiveVendor({
                  variables: { userId: user._id, active_vendor: values.vendor },
                }).then(() => {
                  setVendor(selectedVendor as Vendor);
                  cleanupApolloCache();
                  window.location.reload();
                });
              }
            },
          });
        },
      });
    }

    // Add impersonate option for admin users
    if (user?.role === EnumUserRole.Admin || impersonateUserId) {
      items.push({
        label: impersonateUserId ? 'Stop Impersonating' : 'Impersonate User',
        icon: <PermIdentity />,
        onClick: () => {
          if (impersonateUserId) {
            // Stop impersonating
            clearImpersonation();
            pushToast({
              title: 'Stopped impersonating user',
              body: 'You are now back to your admin account',
              bg: 'info',
            });
            window.location.reload();
          } else {
            // Start impersonating
            save<{ user: any }>({
              type: 'save',
              title: 'Impersonate User',
              initialValues: { user: null },
              saveText: 'Start Impersonating',
              body: <AdminUserSearch nameOverride="user" autoFocus />,
              onSubmit: (values) => {
                const selectedUser = values.user;
                if (selectedUser) {
                  setImpersonateUserId(selectedUser);
                  pushToast({
                    title: 'Impersonating user',
                    body: `Switching to new user profile.`,
                    bg: 'warning',
                  });
                  window.location.reload();
                }
              },
            });
          }
        },
      });
    }
    // add on "key" prop for array rendering
    return items;
  }, [
    allVendors,
    save,
    setVendor,
    updateUserActiveVendor,
    user?._id,
    user?.role,
    impersonateUserId,
    clearImpersonation,
    setImpersonateUserId,
    pushToast,
    cleanupApolloCache,
  ]);

  useEffect(() => {
    tagUserForGTM(user);
    setupLuckyOrange(user);
    tagUserForSentry(user);
    tagUserForIntercom(user);
  }, [user]);

  // Contentsquare waits for auth, then loads once and re-identifies whenever
  // identity changes. The wait is the point: Contentsquare bills by session and
  // a session starts the instant the tag lands, so loading on mount spent one on
  // every staff member and every crawler that reached a public route. Until auth
  // resolves we cannot tell those apart from a processor. See loadContentsquare.
  //
  // Keyed on the identity fields themselves rather than the `user` object so
  // incidental re-renders don't thrash the identify call.
  //
  // `isUserLoaded` is not enough on its own to call someone anonymous: it
  // compares `user?._id` against the query's `_id`, so when the UserTenantVendor
  // query errors BOTH sides are undefined and it reports "loaded" for a signed-in
  // visitor whose profile never arrived. Sampling that visitor as anonymous
  // would latch `hasRequestedLoad` on a ~20% roll, and the retry that finally
  // brings back a staff user would then return at that latch before the
  // internal-email check ever runs — buying the staff session this exists to
  // avoid. Requiring a `user` whenever Cognito says we are authenticated keeps
  // "not signed in" and "not known yet" apart; the latter simply waits.
  const isVisitorKnown =
    !isLoading && isUserLoaded && (!isAuthenticated || Boolean(user));
  useEffect(() => {
    if (!isVisitorKnown) return;

    // Load before identify, not after: injection starts the command queue from
    // empty, so an identify queued first would be thrown away with it.
    loadContentsquare(user);
    identifyContentsquareUser(user, {
      impersonating: Boolean(impersonateUserId),
      userType: contentsquareUserType(user),
      // Vendor only for real processor accounts (own a vendor) — not producers/
      // buyers (arbitrary tenant vendor) or admins (internal, no owned vendor).
      includeVendor: Boolean(user && isProcessor(user) && !isAdmin(user)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isVisitorKnown,
    user?._id,
    user?.role,
    user?.active_vendor?._id,
    impersonateUserId,
  ]);

  // Client-side navigations are invisible to Contentsquare without an
  // artificial pageview. Skips the initial render, which the tag records
  // itself on load.
  const didTrackInitialPageview = useRef(false);
  useEffect(() => {
    if (!didTrackInitialPageview.current) {
      didTrackInitialPageview.current = true;
      return;
    }
    trackContentsquarePageview();
  }, [location.pathname]);

  if (isLoading || isUserLoaded === false) {
    return <FullscreenLoading />;
  }

  // Buyer-only users get a dedicated buyer shell. This must precede the producer
  // branch because isProducer() matches any Basic user (buyers included).
  if (user && isBuyerOnly(user)) {
    return (
      <Suspense fallback={<FullscreenLoading />}>
        <ServiceWorkerProvider />
        {/* Fixed overlay: the pin flies from the search bar on the outgoing page to
            the map on the incoming one, so it must outlive that route change. */}
        <PinFlyer />
        <BuyerShell>
          <PageTowOutlet />
        </BuyerShell>
      </Suspense>
    );
  }

  // Producer users get the self-contained producer shell
  if (isProducer(user)) {
    return (
      <Suspense fallback={<FullscreenLoading />}>
        <ServiceWorkerProvider />
        {/* Fixed overlay: the pin flies from the search bar on the outgoing page to
            the map on the incoming one, so it must outlive that route change. */}
        <PinFlyer />
        <ProducerShell>
          <PageTowOutlet />
        </ProducerShell>
      </Suspense>
    );
  }

  // The register takes the whole screen: no site header or nav, it has its
  // own bar with the way back.
  if (pathname.startsWith('/processor/register')) {
    return (
      <Suspense fallback={<FullscreenLoading />}>
        <ServiceWorkerProvider />
        <PageTowOutlet />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<FullscreenLoading />}>
      <ServiceWorkerProvider />
      {/* Fixed overlay: the pin flies from the search bar on the outgoing page to
            the map on the incoming one, so it must outlive that route change. */}
      <PinFlyer />
      <SiteHeader
        user={
          user
            ? {
                ...user,
                first_name: user.first_name || undefined,
              }
            : undefined
        }
        isProduction={environment.production}
        env={import.meta.env.VITE_ENV}
        title="Partners"
        navLinks={dashboardNavLinks}
        loginFn={handleLogin}
        logoutFn={async () => {
          // Mark logout to trigger cache cleanup on next login
          markLogout();
          await logout();
          navigate('/login');
        }}
        logo={
          <img
            src={!user ? whiteLogo : logo}
            height={39}
            alt={`farmshare ${!user && 'partners'} logo`}
            className="mt-1 text-body"
          />
        }
        dropdownItems={dropdownItems}
        vendorName={vendor?.shop_name}
        globalSearchElement={
          isProcessor(user) && user ? (
            <>
              {/* TEMPORARY (inventory beta): phones' feedback button. */}
              {isProcessorPage && inventory.betaFeedback && (
                <Suspense fallback={null}>
                  <InventoryBetaFeedbackHeaderButton />
                </Suspense>
              )}
              <IconButton
                color="inherit"
                onClick={openGlobalSearch}
                sx={{ display: { xs: 'flex', md: 'none' } }}
              >
                <Search />
              </IconButton>
            </>
          ) : null
        }
      />

      {isProcessorPage && user && canWrite(user) && (
        <Suspense fallback={null}>
          <FabMenu />
        </Suspense>
      )}
      {/* Read-only members included: reporting a problem isn't a write. */}
      {isProcessorPage && user && inventory.betaFeedback && (
        <Suspense fallback={null}>
          <InventoryBetaFeedbackLauncher />
        </Suspense>
      )}
      <Box
        sx={{
          mx: 'auto',
          // dvh, not vh. On iOS `100vh` is the LARGE viewport — the height with
          // the browser toolbar retracted — so while the toolbar is expanded
          // this reserved ~40px more than the screen actually has, and every
          // page picked up a short phantom scroll below its content. `dvh`
          // tracks the viewport as the toolbar comes and goes.
          minHeight: (theme) =>
            `calc(100dvh - ${marginTop}px - ${theme.spacing(2)})`,
          width: '100%',
          // The insets clear the notch on a phone in landscape — which side it
          // is on depends on rotation direction. Padding, so the page colour
          // still fills the notch area. Zero everywhere else.
          pl: `calc(${marginLeft}px + env(safe-area-inset-left, 0px)) !important`,
          pr: 'env(safe-area-inset-right, 0px)',
          transition: 'padding-left 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default',
          '@media print': {
            pl: '0px !important',
            marginTop: 0,
            minHeight: 'auto',
            transition: 'none',
          },
        }}
      >
        <ReadOnlyBanner />
        <PageTowOutlet />
      </Box>
    </Suspense>
  );
}
